import { e as __rest, a as _assign, _ as __makeTemplateObject, I as IntersectionObserver } from './common.js';
import { jsx } from '@emotion/react/jsx-runtime';
import { memo, useCallback, useState, useEffect } from 'react';
import { useTheme, css, keyframes } from '@emotion/react';
import { i as useNodeSizeObserver } from './hooks.js';
import 'qs';
import '@bucketplace/lib';
import { d as calcImageSize, c as getNearestSize, a as getImageUrl, b as getImageSrcSet } from './utils.js';
import { R as RESET_BUTTON, p as pxToRem } from './foundation.js';

var DEFAULT_WIDTH = 240;
var PIXEL_RATIOS = [1.5, 2, 3];
var DEFAULT_RESIZE_IMAGE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAQSURBVHgBAQUA+v8AAAAAAAAFAAFkeJU4AAAAAElFTkSuQmCC';
var ResizeImage = /*#__PURE__*/memo(function (_a) {
  var src = _a.src,
    alt = _a.alt,
    style = _a.style,
    className = _a.className,
    quality = _a.quality,
    objectFit = _a.objectFit,
    placeholderColor = _a.placeholderColor,
    _b = _a.intrinsicInitialWidth,
    intrinsicInitialWidth = _b === void 0 ? DEFAULT_WIDTH : _b,
    intrinsicInitialHeight = _a.intrinsicInitialHeight,
    intrinsicAspect = _a.intrinsicAspect,
    _c = _a.lazy,
    lazy = _c === void 0 ? {
      threshold: 0
    } : _c,
    onError = _a.onError,
    rest = __rest(_a, ["src", "alt", "style", "className", "quality", "objectFit", "placeholderColor", "intrinsicInitialWidth", "intrinsicInitialHeight", "intrinsicAspect", "lazy", "onError"]);
  var theme = useTheme();
  var getImageOptions = useCallback(function () {
    return _assign(_assign({
      src: src,
      quality: quality
    }, calcImageSize({
      width: intrinsicInitialWidth,
      height: intrinsicInitialHeight,
      aspect: intrinsicAspect
    })), intrinsicAspect && {
      mode: 'cover'
    });
  }, [src, intrinsicAspect, intrinsicInitialWidth, intrinsicInitialHeight, quality]);
  var _d = useState(function () {
      return getImageOptions();
    }),
    image = _d[0],
    setImage = _d[1];
  var _e = useState(false),
    isLoaded = _e[0],
    setLoaded = _e[1];
  useEffect(function () {
    setImage(getImageOptions());
    setLoaded(false);
  }, [src, intrinsicAspect, getImageOptions]);
  var handleChangeImgNodeSize = useCallback(function (size) {
    if (size.width === 0 || size.height === 0 || !isLoaded) return;
    setImage(function (image) {
      if (size.width > (image.width || 0) || size.height > (image.height || 0)) {
        var width = getNearestSize(size.width);
        var height = Math.ceil(width * size.height / size.width);
        return _assign(_assign(_assign({}, image), typeof image.width !== 'undefined' && {
          width: width
        }), typeof image.height !== 'undefined' && {
          height: height
        });
      }
      return image;
    });
  }, [isLoaded]);
  var imgNodeRef = useNodeSizeObserver(handleChangeImgNodeSize, [isLoaded]);
  var imgCSS = /*#__PURE__*/css(templateObject_3$2 || (templateObject_3$2 = __makeTemplateObject(["\n    ", "\n    ", "\n  " + (process.env.NODE_ENV === "production" ? "" : ";label:imgCSS;")], ["\n    ", "\n    ", "\n  " + (process.env.NODE_ENV === "production" ? "" : ";label:imgCSS;")])), objectFit && /*#__PURE__*/css(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["\n      width: 100%;\n      height: 100%;\n      object-fit: ", ";\n    " + (process.env.NODE_ENV === "production" ? "" : ";label:imgCSS;")], ["\n      width: 100%;\n      height: 100%;\n      object-fit: ", ";\n    " + (process.env.NODE_ENV === "production" ? "" : ";label:imgCSS;")])), objectFit), !isLoaded && /*#__PURE__*/css(templateObject_2$2 || (templateObject_2$2 = __makeTemplateObject(["\n      background-color: ", ";\n    " + (process.env.NODE_ENV === "production" ? "" : ";label:imgCSS;")], ["\n      background-color: ", ";\n    " + (process.env.NODE_ENV === "production" ? "" : ";label:imgCSS;")])), placeholderColor || theme.content.base4));
  var _f = useState(!lazy),
    isLazyRendered = _f[0],
    setLazyRendered = _f[1];
  if (!isLazyRendered) {
    return jsx(IntersectionObserver, _assign({
      onIntersect: function onIntersect() {
        return setLazyRendered(true);
      }
    }, lazy, {
      style: style,
      className: className
    }, {
      children: jsx("img", {
        // 1x1 transparent image
        src: DEFAULT_RESIZE_IMAGE,
        style: style,
        css: imgCSS,
        className: className,
        alt: "",
        "aria-label": rest['aria-label']
      })
    }));
  }
  var imageSrc = getImageUrl(image);
  var imageSrcSet = getImageSrcSet(image, PIXEL_RATIOS);
  return jsx("img", {
    src: imageSrc,
    srcSet: imageSrcSet,
    ref: imgNodeRef,
    alt: alt,
    style: style,
    css: imgCSS,
    className: className,
    onError: onError,
    onLoad: function onLoad() {
      return setLoaded(true);
    },
    "aria-label": rest['aria-label']
  });
});
var templateObject_1$2, templateObject_2$2, templateObject_3$2;

function cssDotColors(_a) {
  var dotBackgroundColor = _a.dotBackgroundColor,
    boxShadow = _a.boxShadow;
  return /*#__PURE__*/css(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n    background-color: ", ";\n    box-shadow: ", ";\n  " + (process.env.NODE_ENV === "production" ? "" : ";label:cssDotColors;")], ["\n    background-color: ", ";\n    box-shadow: ", ";\n  " + (process.env.NODE_ENV === "production" ? "" : ";label:cssDotColors;")])), dotBackgroundColor, boxShadow);
}
function cssCurrentDot(_a) {
  var activeDotBackgroundColor = _a.activeDotBackgroundColor,
    boxShadow = _a.boxShadow;
  return /*#__PURE__*/css(templateObject_2$1 || (templateObject_2$1 = __makeTemplateObject(["\n    background-color: ", ";\n    box-shadow: ", ";\n  " + (process.env.NODE_ENV === "production" ? "" : ";label:cssCurrentDot;")], ["\n    background-color: ", ";\n    box-shadow: ", ";\n  " + (process.env.NODE_ENV === "production" ? "" : ";label:cssCurrentDot;")])), activeDotBackgroundColor, boxShadow);
}
function cssDotSize(size) {
  return /*#__PURE__*/css(templateObject_3$1 || (templateObject_3$1 = __makeTemplateObject(["\n    width: ", ";\n    height: ", ";\n    ", ";\n  " + (process.env.NODE_ENV === "production" ? "" : ";label:cssDotSize;")], ["\n    width: ", ";\n    height: ", ";\n    ", ";\n  " + (process.env.NODE_ENV === "production" ? "" : ";label:cssDotSize;")])), pxToRem(size), pxToRem(size), size === 0 ? "margin: ".concat(pxToRem(0)) : '');
}
function makeIndicatorDots(settings) {
  var dotSize = settings.dotSize;
  return function (_a) {
    var maxPage = _a.maxPage,
      _b = _a.currentPage,
      currentPage = _b === void 0 ? 1 : _b,
      _onClick = _a.onClick,
      rest = __rest(_a, ["maxPage", "currentPage", "onClick"]);
    var _c = useState(currentPage - 1),
      currentIndex = _c[0],
      setCurrentIndex = _c[1];
    var isCurrentDot = function isCurrentDot(index) {
      return index === currentIndex;
    };
    useEffect(function () {
      setCurrentIndex(currentPage - 1);
    }, [currentPage]);
    if (maxPage <= 1) return null;
    return jsx("ul", _assign({
      css: /*#__PURE__*/css(templateObject_4$1 || (templateObject_4$1 = __makeTemplateObject(["\n        text-align: center;\n        white-space: nowrap;\n        font-size: 0;\n        line-height: 0;\n        padding: 0;\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")], ["\n        text-align: center;\n        white-space: nowrap;\n        font-size: 0;\n        line-height: 0;\n        padding: 0;\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")])))
    }, rest, {
      children: Array(maxPage).fill(0).map(function (_, index) {
        return jsx("li", _assign({
          css: /*#__PURE__*/css(templateObject_5$1 || (templateObject_5$1 = __makeTemplateObject(["\n                margin: 0;\n                padding: 0;\n                height: 0;\n                vertical-align: bottom;\n                display: inline-block;\n              " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")], ["\n                margin: 0;\n                padding: 0;\n                height: 0;\n                vertical-align: bottom;\n                display: inline-block;\n              " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")])))
        }, {
          children: jsx("button", {
            type: "button",
            "aria-label": "".concat(index + 1, " \uD398\uC774\uC9C0"),
            "aria-current": isCurrentDot(index) ? 'true' : undefined,
            onClick: function onClick() {
              setCurrentIndex(index);
              _onClick === null || _onClick === void 0 ? void 0 : _onClick(index);
            },
            css: [/*#__PURE__*/css(templateObject_6$1 || (templateObject_6$1 = __makeTemplateObject(["\n                    ", "\n                    border-radius: 50%;\n                    transition: 0.15s background-color, 0.15s width, 0.15s height;\n                    transform: translateY(-50%);\n                    box-sizing: border-box;\n                    margin: 0 ", ";\n                  " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")], ["\n                    ", "\n                    border-radius: 50%;\n                    transition: 0.15s background-color, 0.15s width, 0.15s height;\n                    transform: translateY(-50%);\n                    box-sizing: border-box;\n                    margin: 0 ", ";\n                  " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")])), RESET_BUTTON, pxToRem(3)), cssDotColors(settings), isCurrentDot(index) && cssCurrentDot(settings), cssDotSize(dotSize(index, maxPage, currentIndex))]
          })
        }), "dot-".concat(index));
      })
    }));
  };
}
var templateObject_1$1, templateObject_2$1, templateObject_3$1, templateObject_4$1, templateObject_5$1, templateObject_6$1;

var rotate = keyframes(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  from {\n    transform: rotate(0deg);\n  }\n  to {\n    transform: rotate(360deg);\n  }\n"], ["\n  from {\n    transform: rotate(0deg);\n  }\n  to {\n    transform: rotate(360deg);\n  }\n"])));
function cssVariant(theme, variant, onDimmed) {
  switch (variant) {
    case 'default':
      return /*#__PURE__*/css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n        border-color: ", ";\n        border-top-color: ", ";\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;")], ["\n        border-color: ", ";\n        border-top-color: ", ";\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;")])), theme.dim.onDark.thick, theme.content.white);
    case 'white':
      return /*#__PURE__*/css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n        border-color: ", ";\n        border-top-color: ", ";\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;")], ["\n        border-color: ", ";\n        border-top-color: ", ";\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;")])), theme.dim.onDark.basic, theme.content.white);
    case 'base1':
      return /*#__PURE__*/css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n        border-color: ", ";\n        border-top-color: ", ";\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;")], ["\n        border-color: ", ";\n        border-top-color: ", ";\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;")])), onDimmed ? theme.content.base6 : theme.content.base5, theme.content.base1);
    case 'primary1':
    case 'primary2':
    case 'primary3':
    case 'primary4':
      return /*#__PURE__*/css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n        border-color: ", ";\n        border-top-color: ", ";\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;")], ["\n        border-color: ", ";\n        border-top-color: ", ";\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:cssVariant;")])), onDimmed ? theme.content.base6 : theme.content[variant].thin, theme.content[variant].basic);
  }
}
function Loading(_a) {
  var _b = _a.disabled,
    disabled = _b === void 0 ? false : _b,
    _c = _a.variant,
    variant = _c === void 0 ? 'default' : _c,
    _d = _a.onDimmed,
    onDimmed = _d === void 0 ? false : _d,
    rest = __rest(_a, ["disabled", "variant", "onDimmed"]);
  var theme = useTheme();
  return jsx("div", _assign({}, rest, {
    css: /*#__PURE__*/css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n        display: inline-block;\n        width: ", ";\n        height: ", ";\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")], ["\n        display: inline-block;\n        width: ", ";\n        height: ", ";\n      " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")])), pxToRem(24), pxToRem(24))
  }, {
    children: jsx("div", {
      css: [/*#__PURE__*/css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n            border-radius: 50%;\n            width: 100%;\n            height: 100%;\n            border: ", " solid;\n            animation: ", " 1.1s infinite linear;\n            box-sizing: border-box;\n          " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")], ["\n            border-radius: 50%;\n            width: 100%;\n            height: 100%;\n            border: ", " solid;\n            animation: ", " 1.1s infinite linear;\n            box-sizing: border-box;\n          " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")])), pxToRem(2), rotate), disabled ? /*#__PURE__*/css(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n                border-color: ", ";\n                border-top-color: ", ";\n              " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")], ["\n                border-color: ", ";\n                border-top-color: ", ";\n              " + (process.env.NODE_ENV === "production" ? "" : ";label:css;")])), theme.content.base3, theme.content.white) : cssVariant(theme, variant, onDimmed)]
    })
  }));
}
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8;

export { DEFAULT_RESIZE_IMAGE as D, Loading as L, ResizeImage as R, makeIndicatorDots as m };
//# sourceMappingURL=Loading.js.map
